
import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Table,
  TableHead,
  TableCell,
  TableBody,
  TableRow,
  TableContainer,
  CircularProgress,
  Typography,
  TablePagination,
} from "@mui/material";
import api from "../../services/api";


const InstituteSummaryTable = () => {
  const [filteredInstitutes, setFilteredInstitutes] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [selectedInstitutes, setSelectedInstitutes] = useState([]);
  const [tempSelected, setTempSelected] = useState([]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [instituteOpen, setInstituteOpen] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // ------------------- Fetch Institutes -------------------
  const fetchInstitutes = async () => {
    try {
      const accessToken = localStorage.getItem("accessToken");
      const res = await api.get("/event/filter/state-institutes", {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const institutes = res.data?.active_institutes || [];
      setFilteredInstitutes(institutes);
    } catch (err) {
      console.log("Fetch error:", err);
    }
  };

  useEffect(() => {
    fetchInstitutes();
  }, []);

  const searchedInstitutes = filteredInstitutes.filter((inst) =>
    inst.institute_name.toLowerCase().includes(searchText.toLowerCase())
  );

  // ------------------- Fetch Institute Summary -------------------
  const fetchInstituteSummary = async (ids) => {
    if (!ids.length) return;
    try {
      setLoading(true);
      const accessToken = localStorage.getItem("accessToken");
      const res = await api.post(
        "/event/institutes-summary",
        { institute_ids: ids },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      setData(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedInstitutes.length > 0) {
      fetchInstituteSummary(selectedInstitutes);
    }
  }, [selectedInstitutes]);

  // ------------------- Actions -------------------
  const toggleCheckbox = (id) => {
    setTempSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };


  // Pagination
  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: "bold",color:"#4170adff"}}>
        Institute Summary
      </Typography>

      {/* ------------------- INSTITUTE FILTER ------------------- */}
      <Paper sx={{ p: 3, mb: 3 }}>
        {/* ------------------- CUSTOM DROPDOWN ------------------- */}
        <div className="relative w-72">
          <button
            onClick={() => {
              setInstituteOpen(!instituteOpen);
              if (!instituteOpen) setTempSelected(selectedInstitutes); // restore previous applied selection
            }}
            className="flex items-center justify-between w-60 border px-4 py-2 rounded-lg bg-white shadow-sm hover:shadow transition"
          >
            <span>Select Institutes</span>

            {(instituteOpen ? tempSelected.length : selectedInstitutes.length) > 0 && (
              <span className="text-xs bg-blue-600 text-white px-2 py-0.5 rounded-full">
                {instituteOpen ? tempSelected.length : selectedInstitutes.length}
              </span>
            )}


            <svg
              className="w-4 h-4 text-gray-600"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {/* DROPDOWN PANEL */}
          {instituteOpen && (
            <div className="absolute z-20 mt-2 w-72 bg-white border rounded-lg shadow-lg p-3">
              
              {/* SEARCH */}
              <input
                type="text"
                placeholder="Search institutes..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="w-full border px-2 py-1 rounded-md text-sm mb-2 focus:outline-none focus:ring focus:ring-blue-200"
              />

              {/* SELECT ALL / CLEAR */}
              <div className="flex justify-between items-center mb-2">
                <button
                  className="text-xs text-blue-600"
                  onClick={() =>
                    setTempSelected(searchedInstitutes.map((i) => i.institute_id))
                  }
                >
                  Select All
                </button>

                <button
                  className="text-xs text-red-500"
                  onClick={() => {
                    setTempSelected([]);
                    setSelectedInstitutes([]);     // <-- this removes the 2 badge
                  }}
                >
                  Clear
                </button>
              </div>

              {/* LIST */}
              <div className="max-h-40 overflow-y-auto space-y-1">
                {searchedInstitutes.map((inst, i) => (
                  <label key={i} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={tempSelected.includes(inst.institute_id)}
                      onChange={() => toggleCheckbox(inst.institute_id)}
                    />
                    {inst.institute_name}
                  </label>
                ))}
              </div>

              {/* BUTTONS */}
              <div className="flex gap-2 mt-3">

                {/* CLOSE ONLY */}
                <button
                  onClick={() => {
                    setInstituteOpen(false);
                    setTempSelected(selectedInstitutes); // reset to applied values
                  }}
                  className="w-1/2 bg-gray-200 text-gray-700 py-1.5 rounded-md text-sm hover:bg-gray-300"
                >
                  Close
                </button>

                {/* APPLY */}
                <button
                  disabled={tempSelected.length === 0}
                  onClick={() => {
                    setSelectedInstitutes(tempSelected);
                    setInstituteOpen(false);
                  }}
                  className={`w-1/2 py-1.5 rounded-md text-sm text-white 
                    ${tempSelected.length === 0 ? "bg-blue-300 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"}`}
                >
                  Apply
                </button>

              </div>
            </div>
          )}
        </div>


      </Paper>

      {/* ------------------- SUMMARY TABLE ------------------- */}
      {loading ? (
        <Box sx={{ textAlign: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead sx={{ background: "#E8F2FF" }}>
                <TableRow>
                  <TableCell>Institute Name</TableCell>
                  <TableCell>Student limit approved</TableCell>
                  <TableCell>Student limit used</TableCell>
                  <TableCell>Tech camp done</TableCell>
                  <TableCell>Students Trained</TableCell>
                  <TableCell>Fdp done</TableCell>
                  <TableCell>FDP Faculty trained</TableCell>
                  <TableCell>Edp done</TableCell>
                  <TableCell>EDP Faculty trained</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {data.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center">
                      {selectedInstitutes.length === 0
                        ? "Please select one or more institutes to view the summary."
                        : "No summary available for the selected institute(s)."}
                    </TableCell>
                  </TableRow>
                ) : (
                  data
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row) => (
                      <TableRow key={row.institute_id}>
                        <TableCell>{row.institute_name}</TableCell>
                        <TableCell>{row.registration_limit}</TableCell>
                        <TableCell>{row.student_intake_count}</TableCell>
                        <TableCell>{row.techcamp_count}</TableCell>
                        <TableCell>{row.students_trained}</TableCell>
                        <TableCell>{row.fdp_hosted}</TableCell>
                        <TableCell>{row.fdp_educators_trained}</TableCell>
                        <TableCell>{row.edp_count}</TableCell>
                        <TableCell>{row.edp_educators_trained}</TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {data.length > 0 && (
            <TablePagination
              component="div"
              count={data.length}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
            />
          )}
        </Paper>
      )}
    </Box>
  );
};

export default InstituteSummaryTable;
